import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { Application, Canvas, Container } from '@tencent/alloy-renderer';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { LogicService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-logic/base/logic-service';
import { buildTreeSync } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/build-tree-sync';
import { IWrapperComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/types';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import { ILayoutModel } from '@src/slide/domain/ui-model/element-modeles/Interface/layout-model.interface';
import { reaction } from 'mobx';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';
import { IRenderProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer.interface';
import { IFormatBrushManager } from '@src/slide/application/service/format-brush/core/interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { IInfrastructureBase } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';
import type PreviewService from '@src/slide/application/service/preview-service';
import { IFontManager } from '@src/slide/content-viewer/service/font-manager/core/font-manager.interface';
import { ILinkManager } from '@src/slide/application/service/link-manager/core/link-manager.interface';
import {
    IMasterLayoutEditService,
} from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { IContainerRawRenderData } from '@tencent/alloy-renderer/dist/raw-render-data';
import { IGifService } from '@src/slide/application/service/gif-service/index.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';

export class InheritElementsLayer extends Container {
    private slideModel: ISlideModel;
    private getPageScale: () => number;
    private getApplication?: () => Application;
    private elements: IShapeModel[] = [];
    private inheritType;
    private region: string;
    private fromScale: number;
    private fromMasterLayout: boolean;
    private masterLayoutReaction;
    private slideNumberReaction;
    private variable: boolean;
    private uiDataService: IUiDataService;
    private mediaManager?: IMediaManager;
    private editingStateManager?: EditingStateManager;
    private formatBrushManager?: IFormatBrushManager;
    private handleStableLayerUpdate?: (props: Partial<IRenderProps>) => void;
    private infrastructureService: IInfrastructureBase;
    private previewService?: PreviewService;
    private fontManager?: IFontManager;
    private linkManager?: ILinkManager;
    private gifService?: IGifService;
    private selectionService?: ISelectionService;
    private masterLayoutEditService?: IMasterLayoutEditService;

    public constructor(
        inheritType: string,
        slideModel: ISlideModel,
        getPageScale: () => number,
        getApplication: (() => Application) | undefined,
        region: string,
        fromScale: number,
        services: {
            infrastructureService: IInfrastructureBase,
            uiDataService: IUiDataService,
            mediaManager?: IMediaManager,
            linkManager?: ILinkManager,
            editingStateManager?: EditingStateManager,
            formatBrushManager?: IFormatBrushManager,
            previewService?: PreviewService,
            fontManager?: IFontManager,
            masterLayoutEditService?: IMasterLayoutEditService,
            gifService?: IGifService,
            selectionService?: ISelectionService,
        },
        fromMasterLayout?: boolean,
        variable?: boolean,
        handleStableLayerUpdate?: (props: Partial<IRenderProps>) => void,
    ) {
        super();
        const {
            uiDataService, mediaManager, previewService, masterLayoutEditService,
            editingStateManager, formatBrushManager, infrastructureService,
            fontManager, linkManager, gifService, selectionService,
        } = services;
        this.slideModel = slideModel;
        this.getPageScale = getPageScale;
        this.getApplication = getApplication;
        this.infrastructureService = infrastructureService;
        this.uiDataService = uiDataService;
        this.mediaManager = mediaManager;
        this.linkManager = linkManager;
        this.editingStateManager = editingStateManager;
        this.formatBrushManager = formatBrushManager;
        this.previewService = previewService;
        this.fontManager = fontManager;
        this.masterLayoutEditService = masterLayoutEditService;
        this.inheritType = inheritType;
        this.region = region;
        this.fromScale = fromScale;
        this.fromMasterLayout = !!fromMasterLayout;
        this.gifService = gifService;
        this.selectionService = selectionService;
        this.variable = !!variable;
        this.handleStableLayerUpdate = handleStableLayerUpdate;
        this.addMasterLayoutReaction();
        // eslint-disable-next-line no-underscore-dangle
        this._addUpdateListener(this.onUpdate);
    }

    public dispose(): void {
        super.dispose();
        this.masterLayoutReaction?.();
        this.slideNumberReaction?.();
        this.getPageScale = undefined as any;
        this.getApplication = undefined;
    }

    /**
     * 更新slideModel进而更新渲染层
     * @param slideModel
     * @param inheritType
     */
    public updateSlideModel(slideModel: ISlideModel, inheritType: string): void {
        this.slideModel = slideModel;
        this.inheritType = inheritType;

        // 监听母版版式的所有不可变元素，当其变为幻灯片编号时需要通知 Layer 重新渲染
        // 幻灯片编号和其他不可变元素不在同一个 Layer 上渲染。存在一种情况：文本框变为幻灯片编号，这种情况下所有 Layer 都需要 rebuild。
        const elements = this.getChildrenOmitPL();
        if (elements) {
            this.slideNumberReaction?.();
            this.slideNumberReaction = reaction(
                () => elements.map(ele => ele?.hasSlideNum),
                () => this.rebuildChildren(),
            );
        }

        this.rebuildChildren();
    }

    public getSlideMode(): ISlideModel {
        return this.slideModel;
    }

    public getElements(): Array<IElementBaseModel> {
        return this.elements;
    }

    public getInheritType(): string {
        return this.inheritType;
    }

    public generateRawRenderData(canvas: Canvas<unknown>): IContainerRawRenderData {
        return super.generateRawRenderData(canvas);
    }

    private onUpdate = () => {
        this.handleStableLayerUpdate?.({
            slideModel: this.slideModel,
            fromInheritLayer: true,
        });
    };

    /**
     * 获取master或者layout Model
     * @private
     */
    private getMasterLayoutModel(): IMasterModel | ILayoutModel | undefined {
        const presentationModel = this.slideModel.presentationRef;
        if (presentationModel && this.slideModel.sdkData) {
            if (this.inheritType === UIModelType.MASTER) {
                const id = presentationModel.getMasterChildrenMapKey?.(this.slideModel.sdkData);
                return presentationModel.masterList?.filter(e => e?.id === id)[0];
            } if (this.inheritType === UIModelType.LAYOUT) {
                const id = presentationModel.getLayoutChildrenMapKey?.(this.slideModel.sdkData);
                return presentationModel.layoutList?.filter(e => e?.id === id)[0];
            }
        }
    }

    /**
     * 监听master/layout数据转换是否完成，若改变则说明是异步转换完成，需重建
     * @private
     */
    private addMasterLayoutReaction() {
        const model = this.getMasterLayoutModel();
        if (model) {
            this.masterLayoutReaction = reaction(() => model.childrenConverted, () => this.rebuildChildren());
            return;
        }
        // 没有 model 的时候等数据转换完再来一次监听
        eventBus.once(EventKeys.ALL_SLIDE_DATA_CONVERT, () => {
            const model = this.getMasterLayoutModel();
            if (model) {
                if (model.childrenConverted) {
                    this.rebuildChildren();
                } else {
                    this.masterLayoutReaction = reaction(() => model.childrenConverted, () => this.rebuildChildren());
                }
            }
        });
    }

    private rebuildChildren() {
        let elements = this.getChildrenOmitPL() ?? [];
        if (this.variable) {
            elements = elements?.filter(element => element?.hasSlideNumber?.());
        } else {
            elements = elements?.filter(element => !element?.hasSlideNumber?.());
        }

        // 同步models与canvas树
        this.elements = elements;
        const logicService = LogicService.getInstance();
        buildTreeSync({
            canvasLayer: this,
            canvasArr: this.children as IWrapperComponent[],
            modelsArr: this.elements,
            componentFactoryOptions: {
                model: null,
                getPageScale: this.getPageScale,
                getApplication: this.getApplication,
                region: this.region,
                fromScale: this.fromScale,
                logicService,
                fromMasterLayout: this.fromMasterLayout,
                slideModel: this.slideModel,
                infrastructureService: this.infrastructureService,
                uiDataService: this.uiDataService,
                mediaManager: this.mediaManager,
                editingStateManager: this.editingStateManager,
                formatBrushManager: this.formatBrushManager,
                previewService: this.previewService,
                fontManager: this.fontManager,
                linkManager: this.linkManager,
                selectionService: this.selectionService,
                masterLayoutEditService: this.masterLayoutEditService,
                gifService: this.gifService,
            },
        });
    }

    private getChildrenOmitPL(): IShapeModel[] | undefined {
        const presentationModel = this.slideModel.presentationRef;
        if (presentationModel && this.slideModel.sdkData) {
            if (this.inheritType === UIModelType.MASTER) {
                return presentationModel.getMasterChildrenOmitPLByModel?.(this.slideModel) as IShapeModel[];
            }
            if (this.inheritType === UIModelType.LAYOUT) {
                return presentationModel.getLayoutChildrenOmitPLByModel?.(this.slideModel) as IShapeModel[];
            }
        }
    }
}
